import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { useLLMChatBV } from '@ltvco/refresh-lib/hooks';
import { Record as TeaserRecord, useSession } from '@ltvco/refresh-lib/v1';
import { useContext, useState } from 'react';

import {
  FUNCTION_CALL_OUTPUT,
  GENERIC_ERROR_MESSAGE,
  IS_LOCAL_TESTING,
  LOADING_STATUS,
  RESPONSE_TYPE,
  UNSUPPORTED_SEARCH_ERROR_MESSAGE,
  USE_OPENAI_LOCAL_CONFIGURATION,
} from 'components/ChatBV/constants';
import {
  sendMessageToProxy,
  sendMessageToResponsesAPI,
} from 'components/ChatBV/services';
import {
  GenericLLMResponse,
  LoadingStatus,
  Message,
  UserOrientedResponse,
  GenericLLMRequest,
  GenericLLMRequestItem,
  PersonParamsFunctionArguments,
  GeneratedReport,
  GenericReportResponse,
  LLMRequestItem,
  ReponsesAPIRequestProps,
  ResponsesAPIResponse,
} from 'components/ChatBV/types';
import {
  mapLLMServiceResponseToGenericLLMResponse,
  mapResponsesAPIResponseToProcessedResponse,
  handleFunctionCalls,
  buildLLMOrientedRequest,
} from 'components/ChatBV/utils';

import { useLLMWebSearch } from './useLLMWebSearch';

export const useChatConversation = () => {
  const [userOrientedMessages, setUserOrientedMessages] = useState<Message[]>(
    []
  );
  const [lastContextId, setLastContextId] = useState<string | undefined>(
    undefined
  );
  const [generatedReports, setGeneratedReports] = useState<
    Record<string, GeneratedReport>
  >({});
  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(
    LOADING_STATUS.IDLE
  );

  const { trackEventGA4, logError } = useContext(AppConfig);
  const { session, setSession, useAccount } = useSession();
  const { refetch: refetchAccount } = useAccount(session.isAuthenticated);

  const searchChatBV = useLLMChatBV({
    shouldSuspendExecution: false,
  });

  const searchWebSearch = useLLMWebSearch();

  const refetchAccountAfterReportGeneration = async (
    wasThePreviousRequestAFunctionCall: boolean,
    permalink: string[]
  ) => {
    // Only refetch if we actually processed function calls and generated reports
    if (wasThePreviousRequestAFunctionCall && permalink.length > 0) {
      try {
        const { data: accountData } = await refetchAccount();
        setSession({
          account: accountData,
        });
      } catch (error) {
        logError(
          'Error refetching account after report generation',
          error as Error
        );
      }
    }
  };

  const sendMessageToLLM = async (
    request: GenericLLMRequest
  ): Promise<GenericLLMResponse> => {
    const llmOrientedRequest = buildLLMOrientedRequest(request);

    if (USE_OPENAI_LOCAL_CONFIGURATION) {
      let response: ResponsesAPIResponse | null = null;

      if (IS_LOCAL_TESTING) {
        response = await sendMessageToResponsesAPI(
          llmOrientedRequest as ReponsesAPIRequestProps,
          logError
        );
      } else {
        // buildLLMOrientedRequest returns ReponsesAPIRequestProps when USE_OPENAI_LOCAL_CONFIGURATION is true
        const requestProps = llmOrientedRequest as ReponsesAPIRequestProps;
        const inputArray = requestProps.input || [];
        response = await sendMessageToProxy(
          {
            input: inputArray,
            previous_response_id:
              requestProps.previous_response_id ||
              request.context_id ||
              undefined,
          },
          logError
        );
      }

      if (response.type === RESPONSE_TYPE.RESPONSE && response?.data) {
        return mapResponsesAPIResponseToProcessedResponse(response.data);
      }
      return {
        context_id: '',
        data: [response.error?.message || GENERIC_ERROR_MESSAGE, null],
        response_id: '',
      };
    }

    const searchHook = request.useWebSearch ? searchWebSearch : searchChatBV;

    const response = await searchHook.mutateAsync(
      llmOrientedRequest as LLMRequestItem
    );

    if (response) {
      if (request.useWebSearch) {
        trackEventGA4({
          event_name: 'smart_chat_interaction',
          type: 'web_search_manual_trigger',
        });
      }

      if (
        request.useWebSearch === false &&
        response.data &&
        'web_searches' in response.data &&
        Array.isArray(response.data.web_searches) &&
        response.data.web_searches.length > 0
      ) {
        trackEventGA4({
          event_name: 'smart_chat_interaction',
          type: 'web_search_auto_trigger',
        });
      }

      return mapLLMServiceResponseToGenericLLMResponse(response);
    }
    return {
      context_id: '',
      data: [GENERIC_ERROR_MESSAGE, null],
      response_id: '',
    };
  };

  const handleConversation = async (
    request: GenericLLMRequest
  ): Promise<UserOrientedResponse> => {
    // used for the LLM request
    let currentRequestItems: GenericLLMRequestItem[] = [...request.items];

    let previousContextId = request.context_id;

    // used in other report types that are not person search
    const reportData: GenericReportResponse[] = [];
    const reportType: string[] = [];
    const permalink: string[] = [];

    // used for person search
    let teaser: TeaserRecord[] = [];
    const params: PersonParamsFunctionArguments[] = [];

    //used to iterate through the LLM request
    let keepIterating = true;
    let wasThePreviousRequestAFunctionCall = false;
    const maxIterations = 3;
    let iterations = 0;
    let executedFunctionCallName: string | undefined;
    // Track the FIRST function call executed (TRIGGER_SEARCH) to determine if we should show summary
    // This is important because subsequent calls (like GET_SPECIFIC_REPORT_SECTION) shouldn't override it
    let firstExecutedFunctionCall: string | undefined;

    while (keepIterating && iterations < maxIterations) {
      // send the LLM request and parse the response

      const response = await sendMessageToLLM({
        items: currentRequestItems,
        context_id: previousContextId,
        useWebSearch: request.useWebSearch,
      });

      // functionCalls should be an array of function calls (tool calls)
      const [answer, functionCalls] = response.data;

      const currentContextId = USE_OPENAI_LOCAL_CONFIGURATION
        ? response.response_id
        : response.context_id;
      // If the response has function calls, we need to process them
      if (
        functionCalls &&
        Array.isArray(functionCalls) &&
        functionCalls.length > 0
      ) {
        setLoadingStatus(LOADING_STATUS.ANALYZE);
        // We set this to true to know that the previous request was a function call, so we need to generate a function call output
        wasThePreviousRequestAFunctionCall = true;

        // Track which function call was executed (for summary detection)
        executedFunctionCallName = functionCalls[0]?.name;

        // This ensures TRIGGER_SEARCH is preserved even if GET_SPECIFIC_REPORT_SECTION is called later
        if (firstExecutedFunctionCall === undefined) {
          firstExecutedFunctionCall = functionCalls[0]?.name;
        }

        // Here we handle the function calls, triggering the Core calls and getting the data
        const processedFunctionCalls = await handleFunctionCalls(
          functionCalls,
          generatedReports
        );
        setLoadingStatus(LOADING_STATUS.PREPARING);

        const detectImageCall = processedFunctionCalls.find(
          (pfc) => pfc.possibleReports !== undefined
        );

        if (detectImageCall?.possibleReports) {
          const possibleReports = detectImageCall.possibleReports;

          return {
            context_id: currentContextId,
            message:
              possibleReports.length === 1
                ? `We detected ${possibleReports[0].search_type} info in your image, so we're submitting a ${possibleReports[0].search_type} report now.`
                : 'I found multiple ways to search using this image. You can continue with a:',
            possibleReports:
              possibleReports.length > 1 ? possibleReports : undefined,
            response_id: response.response_id,
          };
        }

        currentRequestItems = [];

        for (const processedFunctionCall of processedFunctionCalls) {
          if (
            processedFunctionCall?.permalink !== undefined &&
            processedFunctionCall?.reportData !== undefined
          ) {
            setGeneratedReports((prev) => {
              // Remove existing report with same permalink if it exists
              return {
                ...prev,
                [processedFunctionCall.permalink!]: {
                  reportData:
                    processedFunctionCall.reportData as GenericReportResponse,
                  searchType: processedFunctionCall.searchType as string,
                  permalink: processedFunctionCall.permalink as string,
                },
              };
            });
          }

          // Only push to arrays if permalink and reportData are defined
          if (
            processedFunctionCall?.permalink !== undefined &&
            processedFunctionCall?.reportData !== undefined
          ) {
            reportData.push(processedFunctionCall.reportData!);
            reportType.push(processedFunctionCall.searchType!);
            permalink.push(processedFunctionCall.permalink!);
          }

          // If the function call has a teaser, we need to add it to the teaser array
          if (processedFunctionCall?.teaser) {
            teaser = teaser.concat(processedFunctionCall.teaser);
            reportType.push(processedFunctionCall.searchType!);
          }

          // If the function call has a params, we need to add it to the params array
          if (processedFunctionCall?.params) {
            params.push(processedFunctionCall.params);
          }

          const metadata = {
            ...(processedFunctionCall.permalink && {
              permalink: processedFunctionCall.permalink,
            }),
            ...(processedFunctionCall.searchType && {
              searchType: processedFunctionCall.searchType,
            }),
            // Include params for person searches
            ...(processedFunctionCall.params && {
              params: processedFunctionCall.params,
            }),
            // Include teaser indicator for person searches (we'll restore teaser from output if needed)
            ...(processedFunctionCall.teaser &&
              processedFunctionCall.teaser.length > 0 && {
                hasTeaser: true,
              }),
          };
          currentRequestItems.push({
            type: FUNCTION_CALL_OUTPUT,
            call_id: processedFunctionCall.call_id,
            output: processedFunctionCall.output,
            role: null,
            content: null,
            metadata: JSON.stringify(metadata),
          });
        }
        previousContextId = currentContextId;
        iterations++;
        continue;
      } else {
        keepIterating = false;
        setLastContextId(currentContextId);
        setLoadingStatus(LOADING_STATUS.FINISHING);

        await refetchAccountAfterReportGeneration(
          wasThePreviousRequestAFunctionCall,
          permalink
        );

        if (
          wasThePreviousRequestAFunctionCall &&
          reportData.length > 0 &&
          reportType.length > 0 &&
          permalink.length > 0
        ) {
          return {
            context_id: currentContextId,
            message: answer as string,
            sources: response.sources || [],
            reports: permalink.map((permalink, index) => ({
              reportData: reportData[index],
              type: reportType[index],
              permalink,
            })),
            response_id: response.response_id,
            executedFunctionCall:
              firstExecutedFunctionCall || executedFunctionCallName,
          };
        } else if (wasThePreviousRequestAFunctionCall && teaser.length > 0) {
          return {
            context_id: currentContextId,
            message: answer as string,
            sources: response.sources || [],
            teaser,
            params: params[0],
            response_id: response.response_id,
            executedFunctionCall:
              firstExecutedFunctionCall || executedFunctionCallName,
          };
        }
        return {
          context_id: currentContextId,
          message: answer as string,
          response_id: response.response_id,
          executedFunctionCall:
            firstExecutedFunctionCall || executedFunctionCallName,
          sources: response.sources || [],
        };
      }
    }

    await refetchAccountAfterReportGeneration(
      wasThePreviousRequestAFunctionCall,
      permalink
    );

    return {
      context_id: '',
      message: UNSUPPORTED_SEARCH_ERROR_MESSAGE,
      response_id: '',
      executedFunctionCall: undefined,
      sources: [],
    };
  };

  return {
    userOrientedMessages,
    setUserOrientedMessages,
    generatedReports,
    handleConversation,
    lastContextId,
    setLastContextId,
    loadingStatus,
    setLoadingStatus,
    setGeneratedReports,
    sendMessageToLLM,
  };
};
